import { ILogInRequest } from "api/models/request/ILogInRequest";
import { apiInstance, apiInstanceNoToken } from "api/config/axios";
import { ITokenResponse } from "api/models/response/ITokenResponse";
import { IApiResponse } from "api/models/IApiResponse";
import { IForgotPasswordRequest } from "api/models/request/IForgotPasswordRequest";
import { IRegisterInvitedUserRequest } from "api/models/request/IRegisterInvitedUserRequest";
import { getApiResponseData } from "api/services/api-service";
import { IResetPasswordRequest } from "api/models/request/IResetPasswordRequest";
import { LoggedInUserProfile } from "models/LoggedInUserProfile";
import { UserWorkspaceProfile } from "models/UserWorkspaceProfile";
import { IUploadAvatarRequest } from "api/models/request/IUploadAvatarRequest";
import { IChangeDisplayNameRequest } from "api/models/request/IChangeDisplayNameRequest";
import { IChangeEmailRequest } from "api/models/request/IChangeEmailRequest";
import { IConfirmEmailRequest } from "api/models/request/IConfirmEmailRequest";
import { IConnectUsersWithLessonsRequest } from "api/models/request/IConnectUsersWithLessonsRequest";
import { IUpdateUserTagRequest } from "api/models/request/IUpdateUserTagRequest";
import { IGetWorkspacesResponse } from "api/models/response/IGetWorkspacesResponse";
import { IAssignUsersToPatronRequest } from "api/models/request/IAssignUsersToPatronRequest";
import { IInviteUsersRequest } from "api/models/request/IInviteUsersRequest";
import { Role } from "models/Role";
import { ISendMessagesRequest } from "api/models/request/ISendMessagesRequest";
import { IGetUserRowDetailsRequest } from "api/models/request/IGetUserRowDetailsRequest";
import { UserRowDataDetails } from "components/UserRowDetails";
import { IUnassignUserFromPatronRequest } from "api/models/request/IUnassignUserFromPatronRequest";
import { IUnassignUserFromLessonRequest } from "api/models/request/IUnassignUserFromLessonRequest";
import { IMakeUserParticipantRequest } from "api/models/request/IMakeUserParticipantRequest";
import { IMakeUserPatronRequest } from "api/models/request/IMakeUserPatronRequest";
import { IDeleteAssignedUsersRequest } from "api/models/request/IDeleteAssignedUsersRequest";
import { IDeleteUserAvatarRequest } from "api/models/request/IDeleteUserAvatarRequest";
import { IDeleteUserRequest } from "api/models/request/IDeleteUserRequest";
import { IDeleteManyUsersRequest } from "api/models/request/IDeleteManyUsersRequest";
import { IGetUserProfileRequest } from "api/models/request/IGetUserProfileRequest";
import { UserWithBasicTrainingsInfoModel } from "models/UserWithBasicTrainingsInfoModel";
import { IGetUserWithBasicTrainingsProgressesRequest } from "api/models/request/IGetUserWithBasicTrainingsProgressesRequest";
import { InvitationStatus } from "api/models/InvitationStatus";
import { IGetInvitationStatusRequest } from "api/models/request/IGetInvitationStatusRequest";
import { IVerifyResetPasswordRequest } from "api/models/request/IVerifyResetPasswordRequest";
import { IVerifyResetPasswordLinkResponse } from "api/models/response/IVerifyResetPasswordLinkResponse";
import { IGetUserProfilePhoto } from "api/models/request/IGetUserProfilePhoto";
import { IMfaRequiredResponse } from "@api/models/response/IMfaRequiredResponse";
import { IVerifyMfaRequest } from "@api/models/request/IVerifyMfaRequest";
import { PrepareQrResponse } from "@api/models/response/PrepareQrResponse";

const userApiBaseUrl = '/user';

export const getMyProfile = (): Promise<IApiResponse<LoggedInUserProfile>> => {    
    return apiInstance.get(`${userApiBaseUrl}/my-profile`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getProfile = (request: IGetUserProfileRequest  & { workspaceId: string }): Promise<IApiResponse<UserWorkspaceProfile>> => {
    return apiInstance.get(`${userApiBaseUrl}/profile/${request.userId}/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getMyProfilePhoto = (): Promise<IApiResponse<Blob>> => {
    return apiInstance
        .get<Blob>(`${userApiBaseUrl}/my-profile/photo`, { responseType: 'blob' })
        .then((response) => ({
            success: true,
            data: response.data,
            status: response.status,
            message: response.statusText,
            errors: undefined,
        }))
        .catch((error) => ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: error.response?.statusText || "An error occurred while fetching the profile photo",
            errors: error.response?.data?.errors || [
                { code: "UNKNOWN_ERROR", message: "Unexpected error" },
            ],
        }));
};

export const getProfilePhoto = (request: IGetUserProfilePhoto & { workspaceId: string }): Promise<IApiResponse<Blob>> => {
    return apiInstance
        .get<Blob>(`${userApiBaseUrl}/profile/${request.userId}/workspaces/${request.workspaceId}/photo`, { responseType: 'blob' })
        .then((response) => ({
            success: true,
            data: response.data,
            status: response.status,
            message: response.statusText,
            errors: undefined,
        }))
        .catch((error) => ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: error.response?.statusText || "An error occurred while fetching the profile photo",
            errors: error.response?.data?.errors || [{ code: "UNKNOWN_ERROR", message: "Unexpected error" }], // Ensure errors are structured correctly
        }));
};

export const getSuperiors = (request: { workspaceId: string }): Promise<IApiResponse<UserWorkspaceProfile[]>> => {
    const url = `${userApiBaseUrl}/superiors/workspaces/${request.workspaceId}`;
    return apiInstance.get(url).then((response) => {
        return getApiResponseData(response);
    });
}

export const getSubordinates = (request: { workspaceId: string }): Promise<IApiResponse<UserWorkspaceProfile[]>> => {
    const url = `${userApiBaseUrl}/subordinates/workspaces/${request.workspaceId}`;
    return apiInstance.get(url).then((response) => {
        return getApiResponseData(response);
    });
}

export const getUsers = (request: { workspaceId: string }): Promise<IApiResponse<UserWorkspaceProfile[]>> => {
    return apiInstance.get(`${userApiBaseUrl}/user-list/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getWorkspaces = (): Promise<IApiResponse<IGetWorkspacesResponse>> => {
    return apiInstance.get(`${userApiBaseUrl}/workspaces`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getInvitationRoles = (request: { workspaceId: string }): Promise<IApiResponse<Role[]>> => {
    return apiInstance.get(`${userApiBaseUrl}/workspaces/${request.workspaceId}/invitation-roles`).then((response) => {
        return getApiResponseData(response);
    });
}

export const getInvitationStatus = (request: IGetInvitationStatusRequest): Promise<IApiResponse<InvitationStatus>> => {
    return apiInstance.get(`${userApiBaseUrl}/invitations/${request.invitationId}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const getUserRowDetails = (request: IGetUserRowDetailsRequest & { workspaceId: string }): Promise<IApiResponse<UserRowDataDetails>> => {
    return apiInstance.get(`${userApiBaseUrl}/${request.userId}/details/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const getUsersWithBasicTrainingProgresses = (request: IGetUserWithBasicTrainingsProgressesRequest & { workspaceId: string }): Promise<IApiResponse<UserWithBasicTrainingsInfoModel[]>> => {
    const lessonIds = request.lessonIds.join(',');
    return apiInstance.get(`${userApiBaseUrl}/workspaces/${request.workspaceId}/basic-training-progresses?lessonIds=${lessonIds}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const verifyResetPassword = (request: IVerifyResetPasswordRequest): Promise<IApiResponse<IVerifyResetPasswordLinkResponse>> => {
    return apiInstance.post(`${userApiBaseUrl}/verify-reset-password`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const postLogIn = (params: ILogInRequest): Promise<IApiResponse<ITokenResponse | IMfaRequiredResponse>> => {
    return apiInstanceNoToken.post(`${userApiBaseUrl}/login`, params).then((response) => {
        return getApiResponseData(response);
    });
};

export const verifyMfa = (params: IVerifyMfaRequest): Promise<IApiResponse<ITokenResponse>> => {
    return apiInstanceNoToken.post(`${userApiBaseUrl}/verify-mfa`, params).then((response) => {
        return getApiResponseData(response);
    });
}

export const getLoginUsingQR = (): Promise<IApiResponse<PrepareQrResponse>> => {
    return apiInstance.post(`${userApiBaseUrl}/login/prepare-qr`, {}).then((response) => {
        return getApiResponseData(response);
    });
}

export const refreshToken = (): Promise<IApiResponse<ITokenResponse>> => {
    return apiInstanceNoToken.post(`${userApiBaseUrl}/refresh-token`).then((response) => {
        return getApiResponseData(response);
    });
}

export const postLogOut = (): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${userApiBaseUrl}/logout`).then((response) => {
        return getApiResponseData(response);
    });
};

export const forgotPassword = (request: IForgotPasswordRequest): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${userApiBaseUrl}/forgot-password`, request).then((response) => {
        return getApiResponseData(response);
    });
};

export const registerInvitedUser = (request: IRegisterInvitedUserRequest): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${userApiBaseUrl}/register-invited`, request).then((response) => {
        return getApiResponseData(response);
    });
};

export const inviteUsers = (request: IInviteUsersRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${userApiBaseUrl}/workspaces/${request.workspaceId}/invite`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const sendMessages = (request: ISendMessagesRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${userApiBaseUrl}/sendMessages/workspaces/${request.workspaceId}`, { emails: request.emails, subject: request.subject, message: request.message }).then((response) => {
        return getApiResponseData(response);
    });
}


export const assignUsersToPatron = ( request: IAssignUsersToPatronRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/workspaces/${request.workspaceId}/assign-to-patron`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const resetPassword = (request: IResetPasswordRequest): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/reset-password`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const uploadAvatar = (request: IUploadAvatarRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    const url = request.userId ? `${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/image` : `${userApiBaseUrl}/image`;
    return apiInstance.patch(url, { fileBytes: request.fileBytes, fileExtension: request.fileExtension }).then((response) => {
        return getApiResponseData(response);
    });
}

export const changeDisplayName = (request: IChangeDisplayNameRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    const url = request.userId ? `${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/display-name` : `${userApiBaseUrl}/workspaces/${request.workspaceId}/display-name`;
    return apiInstance.patch(url, { newDisplayName: request.newDisplayName }).then((response) => {
        return getApiResponseData(response);
    });
}

export const changeEmail = (request: IChangeEmailRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    const url = request.userId ? `${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/email` : `${userApiBaseUrl}/email`;
    return apiInstance.patch(url, { newEmail: request.newEmail, password: request.password }).then((response) => {
        return getApiResponseData(response);
    });
}

export const confirmEmail = (request: IConfirmEmailRequest): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/confirm-email`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const connectUsersWithLessons = (request: IConnectUsersWithLessonsRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/workspaces/${request.workspaceId}/add-to-lesson`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const updateUserTag = (request: IUpdateUserTagRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/workspaces/${request.workspaceId}/user-tag`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const makeUserParticipant = (request: IMakeUserParticipantRequest  & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/${request.userId}/make-participant/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const makeUserPatron = (request: IMakeUserPatronRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${userApiBaseUrl}/make-patron/workspaces/${request.workspaceId}`, { userId: request.userId, patronType: request.patronType }).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteAvatar = (request: IDeleteUserAvatarRequest & { workspaceId: string }): Promise<IApiResponse<any>> => { 
    const url = request.userId ? `${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/image/` : `${userApiBaseUrl}/image`
    return apiInstance.delete(url).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteUser = (request: IDeleteUserRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteManyUsers = (request: IDeleteManyUsersRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${userApiBaseUrl}/workspaces/${request.workspaceId}/delete-many?userIds=${request.userIds.join(',')}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteAssignedUsers = (request: IDeleteAssignedUsersRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/assigned-users`).then((response) => {
        return getApiResponseData(response);
    });
}

export const disconnectUserWithPatron = (request: IUnassignUserFromPatronRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/patrons/${request.patronId}/unassign`).then((response) => {
        return getApiResponseData(response);
    });
}

export const disconnectUserWithLesson = (request: IUnassignUserFromLessonRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${userApiBaseUrl}/${request.userId}/workspaces/${request.workspaceId}/lessons/${request.lessonId}/unassign`).then((response) => {
        return getApiResponseData(response);
    });
}